/** License expiry state derived from a broker's license expiry date vs today. */
export type LicenseExpiryState = 'expired' | 'expiring' | 'valid';

/**
 * Compute the license expiry state for a broker.
 * - expiry before today → 'expired'
 * - expiry within the next 30 days → 'expiring'
 * - otherwise → 'valid'
 * Returns null when the expiry date is missing or unparseable (render nothing extra).
 */
export function licenseExpiryState(
  licenseValidUntil: string | null | undefined,
  now: Date = new Date()
): LicenseExpiryState | null {
  if (!licenseValidUntil) return null;
  const expiry = new Date(licenseValidUntil);
  if (Number.isNaN(expiry.getTime())) return null;

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysUntilExpiry = Math.floor((expiry.getTime() - startOfToday.getTime()) / msPerDay);

  if (daysUntilExpiry < 0) return 'expired';
  if (daysUntilExpiry <= 30) return 'expiring';
  return 'valid';
}

/**
 * Whole days from today to the licence expiry (negative once passed), or null
 * when the date is missing or unparseable. Null is NOT "expired".
 */
export function licenseDaysLeft(
  licenseValidUntil: string | null | undefined,
  now: Date = new Date()
): number | null {
  if (!licenseValidUntil) return null;
  const expiry = new Date(licenseValidUntil);
  if (Number.isNaN(expiry.getTime())) return null;
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.floor((expiry.getTime() - startOfToday.getTime()) / (24 * 60 * 60 * 1000));
}

/** "Licence expiring in 12 days" / "Licence expired 40 days ago"; null when nothing to say. */
export function licenseExpiryLabel(
  licenseValidUntil: string | null | undefined,
  now: Date = new Date()
): string | null {
  const days = licenseDaysLeft(licenseValidUntil, now);
  if (days === null || days > 30) return null;
  if (days < 0) return `Licence expired ${-days} day${days === -1 ? '' : 's'} ago`;
  if (days === 0) return 'Licence expires today';
  return `Licence expiring in ${days} day${days === 1 ? '' : 's'}`;
}
